import { apiGet, apiSend } from '../../session/client';
import { type Order, type Tracking } from '../orders/orders-api';

/**
 * Everything the Checkout page reads and writes, and nothing it invents.
 *
 * Each call is an existing platform route — the ones the Android app's
 * checkout and payment screens use — plus two small reads added for the
 * website (stage 6): whether one shop delivers to a postcode, and how this
 * deployment takes payment. Every route is keyed on this browser's device
 * handle, which `apiFetch` sends, and on the access token when somebody is
 * signed in. No body carries a price, an amount or a person: the platform
 * quotes, charges and decides whose checkout this is.
 */

const id = (value: string): string => encodeURIComponent(value);

export interface QuotedLine {
  readonly lineId: string;
  readonly listingId: string;
  readonly title: string;
  readonly quantity: number;
  readonly unitPriceMinor: number;
  readonly lineTotalMinor: number;
  readonly imageUrl?: string;
  readonly consultationId?: string;
}

export interface Quote {
  readonly lines: readonly QuotedLine[];
  readonly itemsTotalMinor: number;
  readonly deliveryFeeMinor: number;
  readonly totalMinor: number;
  readonly currency: string;
  readonly takenAt: string;
  readonly expiresAt: string;
}

export interface CheckoutAddress {
  readonly line1: string;
  readonly line2?: string;
  readonly town: string;
  readonly postcode: string;
  readonly countryCode?: string;
}

export interface CheckoutContact {
  readonly name: string;
  readonly email: string;
  readonly phone?: string;
}

export type Fulfilment = 'COLLECTION' | 'DELIVERY';

export interface CheckoutSession {
  readonly id: string;
  readonly cartId: string;
  readonly storefrontId: string;
  readonly quote: Quote;
  readonly fulfilment?: Fulfilment;
  readonly deliveryAddress?: CheckoutAddress;
  readonly contact?: CheckoutContact;
  readonly status: 'DRAFT' | 'READY' | 'PLACED' | 'ABANDONED';
  readonly ready: boolean;
  readonly orderId?: string;
  readonly version: number;
  /** The shop, read at answer time (stage 6). Absent when the platform could not resolve it. */
  readonly storefront?: { readonly id: string; readonly slug: string; readonly name: string };
}

export interface PlacedOrder {
  readonly orderId: string;
  readonly reference: string;
  readonly totalMinor?: number;
}

export interface DeliveryReach {
  readonly storefrontId: string;
  readonly postcode: string;
  readonly displayArea?: string;
  readonly delivers: boolean;
}

export interface PaymentsConfig {
  readonly available: boolean;
  readonly gateway: 'stripe' | 'fake' | 'none';
  readonly testMode: boolean;
  readonly publishableKey?: string;
}

export interface StartedPayment {
  readonly paymentId: string;
  readonly status: string;
  readonly amountMinor: number;
  readonly currency: string;
  /** Opaque. Handed to Stripe.js and never read here. */
  readonly clientToken: string;
  readonly redirectUrl?: string;
}

export interface PennyQuote {
  readonly earn?: { readonly totalUnits: number; readonly unit: string };
}

/** Starts (or resumes) checkout for this browser's basket. Resuming re-prices it. */
export const startCheckout = (): Promise<CheckoutSession> =>
  apiSend<CheckoutSession>('POST', '/consumer/checkout');

export const setFulfilment = (sessionId: string, fulfilment: Fulfilment): Promise<CheckoutSession> =>
  apiSend<CheckoutSession>('POST', `/consumer/checkout/${id(sessionId)}/fulfilment`, { fulfilment });

export const setAddress = (sessionId: string, address: CheckoutAddress): Promise<CheckoutSession> =>
  apiSend<CheckoutSession>('POST', `/consumer/checkout/${id(sessionId)}/address`, {
    line1: address.line1,
    ...(address.line2 !== undefined && address.line2.trim() !== '' ? { line2: address.line2 } : {}),
    town: address.town,
    postcode: address.postcode,
  });

export const setContact = (sessionId: string, contact: CheckoutContact): Promise<CheckoutSession> =>
  apiSend<CheckoutSession>('POST', `/consumer/checkout/${id(sessionId)}/contact`, {
    name: contact.name,
    email: contact.email,
    ...(contact.phone !== undefined && contact.phone.trim() !== '' ? { phone: contact.phone } : {}),
  });

/** Places the order. With a token, the platform links it to the account from the token it verifies. */
export const confirmCheckout = (sessionId: string): Promise<PlacedOrder> =>
  apiSend<PlacedOrder>('POST', `/consumer/checkout/${id(sessionId)}/confirm`, {});

/** Does this shop deliver to this postcode? Read from the platform's own search index. */
export const deliveryReach = (storefrontId: string, postcode: string): Promise<DeliveryReach> =>
  apiGet<DeliveryReach>(
    `/consumer/location/storefronts/${id(storefrontId)}?postcode=${encodeURIComponent(postcode)}`,
  );

export const getPaymentsConfig = (): Promise<PaymentsConfig> =>
  apiGet<PaymentsConfig>('/consumer/payments/config');

/**
 * Opens a payment for an order. The amount is the order's, read by the
 * platform; this sends only the order and the attempt's idempotency key.
 */
export const startPayment = (orderId: string, idempotencyKey: string): Promise<StartedPayment> =>
  apiSend<StartedPayment>('POST', `/consumer/orders/${id(orderId)}/payment`, undefined, {
    headers: { 'idempotency-key': idempotencyKey },
  });

export const getOrder = (orderId: string): Promise<Order> => apiGet<Order>(`/consumer/orders/${id(orderId)}`);

export const getOrderTracking = (orderId: string): Promise<Tracking> =>
  apiGet<Tracking>(`/consumer/orders/${id(orderId)}/tracking`);

/** What a signed-in person earns on this checkout. PENNY may be off: callers treat a failure as "nothing to show". */
export const getPennyQuote = (sessionId: string): Promise<PennyQuote> =>
  apiGet<PennyQuote>(`/consumer/penny/checkout/${id(sessionId)}`);
